// components/SaveAsTemplateModal.tsx
import React, { useState } from "react";
import { supabase } from "../lib/supabaseClient";
import { useAuth } from "../contexts/AuthContext";

interface SaveAsTemplateModalProps {
  open: boolean;
  onClose: () => void;
  tripId: string;
  onSuccess: () => void;
}

export default function SaveAsTemplateModal({
  open,
  onClose,
  tripId,
  onSuccess,
}: SaveAsTemplateModalProps) {
  const { user } = useAuth();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSave = async () => {
    if (!user || !name.trim()) {
      setError("Bitte gib einen Namen ein.");
      return;
    }

    setLoading(true);
    setError("");

    // 1. Neues Template speichern
    const { data: template, error: templateError } = await supabase
      .from("user_packlist_templates")
      .insert([{ user_id: user.id, name: name.trim(), description }])
      .select()
      .single();

    if (templateError) {
      setError(templateError.message);
      setLoading(false);
      return;
    }

    // 2. Alle aktuellen Items aus der Packliste laden
    const { data: items, error: itemsError } = await supabase
      .from("user_packlist_items")
      .select("item_name, category")
      .eq("user_id", user.id)
      .eq("trip_id", tripId);

    if (itemsError) {
      setError(itemsError.message);
      setLoading(false);
      return;
    }

    // 3. Kategorie-IDs auflösen
    const { data: categories } = await supabase
      .from("packlist_categories")
      .select("id, name");

    const categoryMap = new Map(
      (categories ?? []).map((cat) => [cat.name, cat.id])
    );

    // 4. Items als Template-Items speichern
    const inserts =
      items?.map((item, index) => ({
        template_id: template.id,
        item_name: item.item_name,
        category_id: categoryMap.get(item.category) ?? null,
        sort_index: index,
      })) ?? [];

    const { error: insertError } = await supabase
      .from("user_packlist_template_items")
      .insert(inserts);

    if (insertError) {
      setError(insertError.message);
      setLoading(false);
      return;
    }

    setLoading(false);
    onSuccess();
    onClose();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center">
      <div className="bg-[#161b27] rounded-2xl border border-[#2a3347] p-6 w-full max-w-md">
        <h2 className="text-sm font-semibold text-slate-100 mb-5">📁 Als Vorlage speichern</h2>
        <div className="mb-3">
          <label className="block text-[10px] uppercase tracking-widest text-slate-600 mb-1">Name *</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full bg-[#1e2639] border border-[#2a3347] rounded-xl text-slate-100 px-3 py-2 text-sm focus:outline-none focus:border-sky-500/50"
          />
        </div>
        <div className="mb-4">
          <label className="block text-[10px] uppercase tracking-widest text-slate-600 mb-1">Beschreibung</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="w-full bg-[#1e2639] border border-[#2a3347] rounded-xl text-slate-100 px-3 py-2 text-sm focus:outline-none focus:border-sky-500/50"
            rows={3}
          />
        </div>
        {error && <p className="text-xs text-red-400 mb-3">{error}</p>}
        <div className="flex justify-end gap-2">
          <button
            onClick={onClose}
            className="text-xs px-3 py-1.5 rounded-full bg-[#1e2639] text-slate-400 hover:text-slate-200 transition-colors"
          >
            Abbrechen
          </button>
          <button
            onClick={handleSave}
            disabled={loading}
            className="text-xs px-3 py-1.5 rounded-full bg-sky-600 text-white hover:bg-sky-500 transition-colors disabled:opacity-50"
          >
            {loading ? "Speichern…" : "Speichern"}
          </button>
        </div>
      </div>
    </div>
  );
}
